import React, { useContext, useEffect, useState } from 'react';
import { Vehicle, VehicleFilters } from '../../app/models/Vehicle';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import useVehicleService from '../../app/services/hooks/useVehicleService';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import VehicleCreate from './components/VehicleCreate';
import VehicleEdit from './components/VehicleEdit';
import VehicleDataTable from './components/tables/VehicleDataTable';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Vehículos',
        url: '/vehicles',
        isActive: true
    }
];

const Vehicles = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetVehicles, getVehicles, deleteVehicle } = useVehicleService();
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const { navigationPage } = useNavigationPage();

    const [vehicleIdEdit, setVehicleIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [vehicleFilter, setVehicleFilter] = useState<VehicleFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        patent: '',
        vehicle_type: ''
    });

    useEffect(() => {
        getAllVehicles();
    }, [vehicleFilter.page, vehicleFilter.per_page, vehicleFilter.sort, vehicleFilter.order]);

    const getAllVehicles = () => {
        getVehicles(vehicleFilter, {
            onSuccess: (response: ServiceResponse) => {
                setVehicles(response.data.vehicles);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (vehicleId: number) => {
        setShowingEdit(true);
        setVehicleIdEdit(vehicleId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setVehicleIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllVehicles();
    };

    const destroy = (vehicleId: number) => {
        const _text = 'Está a punto de eliminar el vehículo #' + vehicleId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando vehículo...');
                        deleteVehicle(vehicleId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El vehículo no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Vehículos" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo Vehículo" />}
                />
                <VehicleDataTable
                    vehicles={vehicles}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetVehicles}
                    filter={vehicleFilter}
                    setFilter={setVehicleFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Vehículo"
                    backdrop={true}
                    showFooter={false}
                >
                    <VehicleCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Vehículo"
                    backdrop={true}
                    showFooter={false}
                >
                    <VehicleEdit
                        vehicleId={vehicleIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Vehicles;
